import * as React from 'react';
import { GlobalApplicationState } from "globalApplicationState";
import { ConnectedProps, connect } from "react-redux";
import ToggleSwitch from 'modules/common/components/toggleSwitch';
import * as actions from '../actionCreator';
import DeleteIcon from '@mui/icons-material/Delete';
import Select from "react-select";
import { getNotificationChannelsStrings, isAtLeastOneReminderChannelOn, isAtLeastOneReminderDataChannelOn } from 'utils/notificationsHelper';
import { Notifications } from 'modules/posts/models';
import { isEqual } from 'lodash';
import { TenantNotificationSettings } from 'modules/messaging';

interface ComponentProps { 
    defaultName: string
    notificationName: string
}

export interface reminderForInsertion {
    delay: number,
    range: RangeType,
    channel: Channel
}

interface ComponentState {
    reminders: ReminderData[];
    toEditIndex: number;
    remindersEnabled: boolean;

    defaultTimeLocation: string;
    arrayLocation: keyof TenantNotificationSettings;

    teamsEnabled: boolean;
    mobileEnabled: boolean;
    smsEnabled: boolean;
    emailEnabled: boolean;

    isNoAuthoringNotificationChannelsOn: boolean;
}

export enum RangeType {
    Default = "default",
    Everyone = "everyone",
    Fallback = "fallback",
    None = "none",
    Subscribers = "subscribers"
}

export enum Channel {
    Email = "email",
    Teams = "teams",
    SMS = "sms",
    Mobile = "mobile",
    Inherit = "inherit"
}

export interface ReminderData {
    delay: number;
    displayDelay: number;
    daysSelected: boolean;
    
    inherit: boolean;
    teams: boolean;
    mobile: boolean;
    sms: boolean;
    email: boolean;
}

class ReminderPopout extends React.Component<PropsWithRedux, ComponentState> {
    constructor(props) {
        super(props);
        const reminderSettingsKeyName = (this.props.defaultName + "Settings") as keyof TenantNotificationSettings;
        this.state = {
            reminders: [],
            defaultTimeLocation: this.props.defaultName+"Reminder",
            arrayLocation: reminderSettingsKeyName,
            remindersEnabled: isAtLeastOneReminderChannelOn(this.props.notificationSettings, reminderSettingsKeyName),
            toEditIndex: 0,
            teamsEnabled: this.props.notificationSettings.settings[`${this.props.notificationName}TeamsAttempt`]?.toggle ?? false,
            mobileEnabled: this.props.notificationSettings.settings[`${this.props.notificationName}MobileAttempt`]?.toggle ?? false,
            emailEnabled: this.props.notificationSettings.settings[`${this.props.notificationName}EmailAttempt`]?.toggle ?? false,
            smsEnabled: this.props.smsManagementEnabled && (this.props.notificationSettings.settings[`${this.props.notificationName}SMSAttempt`]?.toggle ?? false),
            isNoAuthoringNotificationChannelsOn: true,
        };
    }

    componentDidMount() {
        const defaultNotificationSettings = this.props.notificationSettings[this.state.arrayLocation] as Notifications;
        const reminderSettings = defaultNotificationSettings.reminders;
        const uniqueDelays = Array.from(new Set((reminderSettings.map(e => e.delay))));
        var toLoad = new Array<ReminderData>(uniqueDelays.length);

        for(let i=0; i<uniqueDelays.length; i++) {
            let daySelected = uniqueDelays[i] as number % 24 === 0;
            toLoad[i] = this.getDefaultReminder();
            toLoad[i].delay = uniqueDelays[i] as number;
            toLoad[i].displayDelay = daySelected ? toLoad[i].delay / 24 : toLoad[i].delay;
            toLoad[i].daysSelected = daySelected;
            toLoad[i].inherit = false;
        }

        reminderSettings.forEach(reminder => {
            let currentReminderToEdit = uniqueDelays.indexOf(reminder.delay);

            switch(reminder.channel)
            {
                case Channel.Inherit:
                    toLoad[currentReminderToEdit].inherit = true;
                    break;
                    
                case Channel.Email:
                    toLoad[currentReminderToEdit].email = true;
                    break;

                case Channel.SMS:
                    toLoad[currentReminderToEdit].sms = true;
                    break;

                case Channel.Teams:
                    toLoad[currentReminderToEdit].teams = true;
                    break;

                case Channel.Mobile:
                    toLoad[currentReminderToEdit].mobile = true;
                    break;

                default:
                    console.log("Critical error with loading saved data, investigate tenant settings.")
            }
        });

        this.setState({ reminders: toLoad, isNoAuthoringNotificationChannelsOn: getNotificationChannelsStrings(this.props.notificationSettings[this.state.arrayLocation] as Notifications).length === 0 });
    }

    componentDidUpdate(prevProps: PropsWithRedux) {
        this.props.setReminders(
            this.state.remindersEnabled 
                ? this.state.reminders.filter(reminder => reminder.displayDelay > 0 && (!this.state.isNoAuthoringNotificationChannelsOn || !reminder.inherit)) 
                : [],
                this.state.arrayLocation
        );

        if (!isEqual(
            prevProps.notificationSettings[this.state.arrayLocation] as Notifications, 
            this.props.notificationSettings[this.state.arrayLocation] as Notifications
        )) {
            this.setState({
                isNoAuthoringNotificationChannelsOn: getNotificationChannelsStrings(this.props.notificationSettings[this.state.arrayLocation] as Notifications).length === 0
            });
        }

    }    

    public render() {
        const timeOptions = [
            {label: 'Hours', value: false},
            {label: 'Days', value: true}
        ];

        return(
            <div>
                <div className="authoring-prompt">
                    <div>
                        <h4>Edit Reminder Default Settings</h4>
                        <span className="description-text">The following details will be the default reminder setting for each time a notification is created and/or published.</span>
                    </div>
                </div>

                <div className="authoring-settings-zone">
                    <div>
                        <div style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap"}}>
                            <span>Turn on <i>'Send reminder if post is unread or not complied with'</i> setting when creating new posts</span>
                            <ToggleSwitch
                                onClick={this.onToggleClicked}
                                value={this.state.remindersEnabled}
                            />
                        </div>
                        <span className="description-text">Remind employees if the original notification is still unread or not complied with after a set amount of time.</span>
                    </div>
                    <span className="description-text">Add/edit additional reminder channels:</span>

                    <div className="reminder-spacing">
                        {this.state.remindersEnabled && this.state.reminders.map((reminder, index) => 
                            <div key={index} className="reminder-background">
                                <div>
                                    <span>Reminder {index+1}</span>
                                    {index !== 0 && 
                                        <DeleteIcon className="reminder-trash" onClick={() => this.onDeleteReminder(index)}/>}
                                </div>

                                <div>
                                    <div className="settings-input-label">If unread or not complied with, send reminder after </div>
                                    <input 
                                        className={`settings-input-field-sm ${reminder.displayDelay < 1 ? 'reminder-delay-error' : ''}`}
                                        type="number"
                                        value={reminder.displayDelay}
                                        onChange={(e) => this.updateDelay(index, e.target.valueAsNumber)}
                                        min={1}
                                    ></input>
                                    <Select 
                                        className="settings-input-field-select"
                                        options={timeOptions}
                                        defaultValue={timeOptions[0]}
                                        onChange={(e) => this.toggleTime(e, index)}
                                        value={reminder.daysSelected ? timeOptions[1] : timeOptions[0]}
                                    />
                                </div>

                                <div className="range-channel-selection">
                                    <label>
                                        <input type="radio"
                                            checked={reminder.inherit && !this.state.isNoAuthoringNotificationChannelsOn}
                                            onChange={() => this.toggleRadio(index)}
                                            disabled={this.state.isNoAuthoringNotificationChannelsOn}
                                        />
                                        <span>Use channels selected in authoring notifications</span>
                                    </label>
                                    {!this.state.isNoAuthoringNotificationChannelsOn && this.getInheritChannelsLabel(reminder)}
                                    <label>
                                        <input type="radio"
                                            checked={!reminder.inherit}
                                            onChange={() => this.toggleRadio(index)}
                                        />
                                        <span>Custom channel options</span>
                                    </label>

                                    {!reminder.inherit &&
                                    <div className="checkbox-display-vertical">
                                        {this.state.emailEnabled &&
                                            <label>
                                                <input type="checkbox"
                                                    checked={reminder.email}
                                                    onChange={(e) => this.toggleCheck(e, index)}
                                                    name="email"
                                                /> <span>Email</span>
                                            </label>
                                        }
                                        {this.state.mobileEnabled &&
                                            <label>
                                                <input type="checkbox"
                                                    checked={reminder.mobile}
                                                    onChange={(e) => this.toggleCheck(e, index)}
                                                    name="mobile"
                                                /> <span>Mobile App</span>
                                            </label>
                                        }
                                        {this.state.teamsEnabled && 
                                            <label>
                                                <input type="checkbox"
                                                    checked={reminder.teams}
                                                    onChange={(e) => this.toggleCheck(e, index)}
                                                    name="teams"
                                                /> <span>Teams Chatbot</span>
                                            </label>
                                        }
                                        {this.state.smsEnabled &&
                                            <label>
                                                <input type="checkbox"
                                                    checked={reminder.sms}
                                                    onChange={(e) => this.toggleCheck(e, index)}
                                                    name="sms"
                                                /> <span>SMS</span>
                                            </label>
                                        }
                                        {!isAtLeastOneReminderDataChannelOn(reminder, this.props.notificationSettings, this.state.arrayLocation) &&
                                            <div className='select-channel-text-div'>
                                                <span className="select-channel-text">Select at least one (1) channel</span>
                                            </div>
                                        }
                                    </div>}
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        {this.state.remindersEnabled && this.state.reminders.length < 5 &&
                            <span onClick={this.onAddReminder} className="reminder-text">+ Add reminder</span>
                        }
                    </div>
                </div>
            </div>
        );
    }

    private onToggleClicked = () => {
        const newValue = !this.state.remindersEnabled;

        if (newValue && this.state.reminders.length === 0) {
            this.onAddReminder();
        }

        this.setState({remindersEnabled: newValue});
    }

    private onAddReminder = () => {
        this.setState({ reminders: this.state.reminders.concat([ this.getDefaultReminder() ]) });
    }

    private getDefaultReminder = (): ReminderData => {
        return { 
            delay: this.props.notificationSettings[this.state.defaultTimeLocation] ?? 1, 
            displayDelay: this.props.notificationSettings[this.state.defaultTimeLocation] ?? 1,
            daysSelected: false,
            inherit: !this.state.isNoAuthoringNotificationChannelsOn,
            email: false,
            sms: false,
            teams: false,
            mobile: false
        }
    }

    private onDeleteReminder = (reminderIndex: number) => {
        let reminders: ReminderData[] = this.state.reminders.slice();
        reminders.splice(reminderIndex, 1);
        this.setState({ reminders });
    }

    private updateDelay = (index: number, newValue: number) => {
        const newArray = this.state.reminders;
        newArray[index].displayDelay = newValue;
        this.setState({reminders: newArray});
    }

    private toggleRadio = (index: number) => {
        const newArray = this.state.reminders;
        newArray[index].inherit = !newArray[index].inherit
        this.setState({reminders: newArray});
    }

    private toggleCheck = (e: any, index: number) => {
        const newArray = this.state.reminders;
        newArray[index][e.target.name] = !newArray[index][e.target.name]
        this.setState({reminders: newArray})
    }

    private toggleTime = (e: any, index: number) => {
        const newArray = this.state.reminders;
        newArray[index].daysSelected = e.value
        this.setState({reminders: newArray})
    }

    private getInheritChannelsLabel = (reminderData: ReminderData): React.ReactNode => {
        const reminderChannelsStrings = getNotificationChannelsStrings(this.props.notificationSettings[this.state.arrayLocation] as Notifications);
        return <>
          {
            reminderData.inherit &&
            <div className='reminder-inherit-channels-label'>
              Reminder channel{reminderChannelsStrings.length > 1 ? 's' : ''}: {reminderChannelsStrings.join(', ')}
            </div>
          }
        </>
    }
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        notificationSettings: state.settings.notificationSettings,
        smsManagementEnabled: state.settings.tenantSettings.showFeatures.smsManagementEnabled
    }),
    {
        setReminders: actions.SetRemindersAction,
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default (connector(ReminderPopout));